import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast as Base } from '@base-ui/react/toast';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { cn } from './cn';
import { escapeCloses } from './escape';
import { focusRing } from './focus';

export const TOAST_MS = 2500;
/** A toast with an action (Undo) or an error stays longer: the person has to read, decide and reach the button. */
export const TOAST_ACTION_MS = 6000;
/** More toasts at once than this and the oldest leaves (P15c): they stack instead of covering each other. */
export const TOAST_MAX_VISIBLE = 3;

/** One manager for the page: any `<Toast>` anywhere queues into the one `<ToastHost>` (mounted in providers.tsx). */
export const toastManager = Base.createToastManager();

/** The toasts on screen, oldest first: a new one past TOAST_MAX_VISIBLE closes the oldest (its `onDone` runs). */
const live: string[] = [];

/** What had the focus before someone tabbed into a toast: it gets it back when that toast closes (P16-4). */
let returnTo: HTMLElement | null = null;
const nodeOf = (id: string) => document.querySelector<HTMLElement>(`[data-toast-id="${CSS.escape(id)}"]`);
function restoreFocus(id: string) {
  const active = document.activeElement;
  if (returnTo?.isConnected && (!active || active === document.body || nodeOf(id)?.contains(active))) returnTo.focus();
}

export type ToastVariant = 'info' | 'error';
type ToastProps = {
  message: string;
  onDone: () => void;
  /** A button in the toast ("Undo", P16-4): the toast then is a tab stop and closes on Escape. */
  action?: { label: string; onClick: () => void };
  /** Milliseconds; defaults to TOAST_MS, or TOAST_ACTION_MS with an action or the `error` variant. */
  duration?: number;
  /** `error` is announced assertively (role="alert"). */
  variant?: ToastVariant;
  /** Rendered where it stands, outside the page's stack and its limit: the catalogue's previews only. */
  inline?: boolean;
};

const toastClass = 'os-toast flex items-center gap-3 rounded-full bg-fg px-4 py-2 font-sans text-sm text-bg shadow-float';
const actionClass = 'rounded-full px-2.5 py-1 font-medium text-bg underline-offset-2 hover:underline pointer-coarse:min-h-11';

/**
 * A short message at the bottom of the screen, announced politely to screen readers (an `error`: assertively), gone
 * after 2.5 s (6 s with an action). It stays declarative: mounting it shows the toast, unmounting it closes it (with
 * its exit), `onDone` runs when it closes by itself (timer, swipe down, Escape, its action, the stack limit). It
 * renders into the one `<ToastHost>`, so there must be one mounted (providers.tsx).
 */
export function Toast({ message, onDone, action, duration, variant = 'info', inline = false }: ToastProps) {
  const id = useId();
  const done = useRef(onDone);
  done.current = onDone;
  const mounted = useRef(true);
  const timeout = duration ?? (action || variant === 'error' ? TOAST_ACTION_MS : TOAST_MS);
  const actionClick = useRef(action?.onClick);
  actionClick.current = action?.onClick;
  const actionProps = action ? { children: action.label, onClick: () => actionClick.current?.() } : undefined;

  useEffect(() => {
    if (inline) return;
    mounted.current = true;
    live.push(id);
    toastManager.add({
      id,
      title: message,
      type: variant,
      timeout,
      actionProps,
      // Fires when the toast starts closing (timer, swipe, Escape, action): the caller unmounts it, the exit plays on.
      onClose: () => {
        restoreFocus(id);
        if (mounted.current) done.current();
      },
    });
    while (live.length > TOAST_MAX_VISIBLE) toastManager.close(live.shift()!);
    return () => {
      // The caller dropped it: close silently (no onDone) and let Base UI play the exit.
      mounted.current = false;
      restoreFocus(id);
      const i = live.indexOf(id);
      if (i >= 0) live.splice(i, 1);
      toastManager.close(id);
    };
    // One add per mount; later changes go through the update below.
  }, [id, inline]);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (inline) return;
    toastManager.update(id, { title: message, type: variant, timeout, actionProps });
  }, [id, inline, message, timeout, variant, action?.label]);

  const hasAction = !!action;
  useEffect(() => {
    if (!hasAction || inline) return;
    const onKey = (e: KeyboardEvent) => {
      if (escapeCloses(e, nodeOf(id))) toastManager.close(id);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasAction, id, inline]);

  if (!inline) return null;
  // The catalogue's preview: the markup of a live toast, in place.
  return (
    <div role={variant === 'error' ? 'alert' : 'status'} className={toastClass}>
      {message}
      {action && (
        <button type="button" onClick={action.onClick} className={cn(actionClass, focusRing)}>
          {action.label}
        </button>
      )}
    </div>
  );
}

/**
 * Mounted once, at the top of the tree (providers.tsx): the viewport every `<Toast>` renders into, a fixed stack at the
 * bottom centre above the bottom bar and the safe area. A pointer over any toast pauses every timer (so does a focus
 * inside, a touch, and the window losing focus).
 */
export function ToastHost() {
  const { t } = useTranslation();
  return (
    <Base.Provider toastManager={toastManager} timeout={TOAST_MS} limit={TOAST_MAX_VISIBLE + 1}>
      <Base.Portal>
        <Base.Viewport
          data-toasts=""
          {...{ [CANVAS_UI]: '' }}
          aria-label={t('p18.f.toasts')}
          // Each toast carries its own role (status / alert): the region itself must not announce them a second time.
          aria-live="off"
          className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--chrome-bottom)+var(--safe-bottom))] z-(--layer-toast) flex flex-col items-center gap-2 px-4 outline-none"
        >
          <ToastList />
        </Base.Viewport>
      </Base.Portal>
    </Base.Provider>
  );
}

function ToastList() {
  const { toasts } = Base.useToastManager();
  // Base UI keeps the newest first; the page's stack reads oldest first, newest at the bottom.
  return [...toasts].reverse().map((toast) => (
    <Base.Root
      key={toast.id}
      toast={toast}
      swipeDirection="down"
      data-toast-id={toast.id}
      // Base UI makes a toast a dialog; ours is a status (or an alert) and takes no focus unless it has a button.
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      aria-modal={undefined}
      aria-labelledby={undefined}
      aria-describedby={undefined}
      tabIndex={toast.actionProps ? 0 : -1}
      onFocus={(e) => {
        if (e.relatedTarget instanceof HTMLElement && !e.currentTarget.contains(e.relatedTarget)) returnTo = e.relatedTarget;
      }}
      className={cn(toastClass, 'pointer-events-auto touch-pan-x outline-none')}
    >
      <Base.Title render={<span />}>{toast.title}</Base.Title>
      {toast.actionProps && <Base.Action className={cn(actionClass, focusRing)} />}
    </Base.Root>
  ));
}
